import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { T, champ as champStyle } from '../theme';
import { API } from '../api';
import { MARQUE } from '../marque';
import { IconeCorbeille } from '../components/Icones';
import { SqueletteTableau } from '../components/Squelette';

const STATUT_COLORS = {
  'En cours': { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  'Validé':   { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  'Refusé':   { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
};

const TYPE_LABELS = {
  credit_bail:        'Crédit-bail',
  location_financiere:'Location financière',
  enveloppe:          'Enveloppe',
};

// « Tous » ne veut pas dire « tout » : un dossier refusé est une affaire
// close, elle n'a plus sa place dans un écran de suivi. Elle part dans son
// propre onglet, où on la retrouve sans la chercher au milieu des dossiers
// vivants.
const ONGLET_REFUSES = 'Refusés';
const STATUTS = ['Tous', 'En cours', 'Validé', ONGLET_REFUSES];

// L'onglet porte le pluriel, la ligne porte le statut de la base : les deux
// partagent la même couleur.
STATUT_COLORS[ONGLET_REFUSES] = STATUT_COLORS['Refusé'];

function Dossiers({ user }) {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  // L'écran affichait son état vide (« Aucun dossier pour l'instant ») avant
  // l'arrivée des données : sur une liste qui en contient cinq, on lisait
  // brièvement qu'elle était vide.
  const [chargement, setChargement] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('Tous');
  const [loadingId, setLoadingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [envoisMap, setEnvoisMap] = useState({});
  // Fenêtre de saisie de la commission : elle s'ouvre d'elle-même quand un
  // dossier passe en « Validé », parce que c'est le moment où le montant est
  // connu. Le demander plus tard, c'est ne jamais l'obtenir.
  const [commissionModal, setCommissionModal] = useState(null); // dossier | null
  const [commissionSaisie, setCommissionSaisie] = useState('');
  const [commissionEnvoi, setCommissionEnvoi] = useState(false);

  const [banques, setBanques] = useState([]);

  // Modal envoi
  const [envoiModal, setEnvoiModal] = useState(null); // dossier object | null
  const [destinataires, setDestinataires] = useState([{ nom: '', email: '' }]);
  const [message, setMessage] = useState('');
  const [fichiers, setFichiers] = useState([]);
  const [envoiLoading, setEnvoiLoading] = useState(false);
  const [envoiResultat, setEnvoiResultat] = useState(null);
  const fichierRef = useRef(null);

  const reloadEnvois = () => {
    axios.get(`${API}/envois-dossier`).then(res => {
      const map = {};
      res.data.forEach(e => {
        if (!map[e.dossier_id]) map[e.dossier_id] = [];
        map[e.dossier_id].push(e);
      });
      setEnvoisMap(map);
    }).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/dossiers`)
      .then(res => setDossiers(res.data))
      .catch(() => {})
      .finally(() => setChargement(false));
    axios.get(`${API}/banques`).then(res => setBanques(res.data)).catch(() => {});
    reloadEnvois();
  }, []);

  const refuses = dossiers.filter(d => d.statut === 'Refusé');
  const dossiersFiltres =
    filtreStatut === 'Tous' ? dossiers.filter(d => d.statut !== 'Refusé')
    : filtreStatut === ONGLET_REFUSES ? refuses
    : dossiers.filter(d => d.statut === filtreStatut);

  const ouvrirCommission = (dossier) => {
    setCommissionModal(dossier);
    setCommissionSaisie(dossier.commission != null ? String(dossier.commission) : '');
  };

  const enregistrerCommission = async () => {
    if (!commissionModal) return;
    setCommissionEnvoi(true);
    try {
      // Un champ vidé efface la commission : un dossier repassé en cours ne
      // doit pas continuer à peser sur le chiffre d'affaires prévisionnel.
      const valeur = commissionSaisie.trim() === '' ? null : parseFloat(commissionSaisie);
      const res = await axios.patch(`${API}/dossiers/${commissionModal.id}/commission`,
                                    { commission: Number.isNaN(valeur) ? null : valeur });
      setDossiers(prev => prev.map(d =>
        d.id === commissionModal.id ? { ...d, commission: res.data.commission } : d));
      setCommissionModal(null);
      setToast({ message: 'Commission enregistrée', type: 'success' });
    } catch {
      setToast({ message: "La commission n'a pas pu être enregistrée", type: 'error' });
    }
    setCommissionEnvoi(false);
  };

  const telechargerPDF = async (dossier) => {
    setLoadingId(dossier.id);
    try {
      const full = await axios.get(`${API}/dossiers/${dossier.id}`);
      const data = JSON.parse(full.data.donnees || '{}');
      data._skip_save = true;
      const response = await axios.post(`${API}/dossier-financement-pdf`, data, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier_${dossier.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setToast({ message: 'PDF téléchargé !', type: 'success' });
    } catch {
      setToast({ message: 'Erreur lors de la génération du PDF', type: 'error' });
    }
    setLoadingId(null);
  };

  const changerStatut = async (dossier, statut) => {
    try {
      const res = await axios.patch(`${API}/dossiers/${dossier.id}/statut`, { statut });
      setDossiers(prev => prev.map(d => d.id === dossier.id ? { ...d, statut: res.data.statut } : d));
      setToast({ message: 'Statut mis à jour', type: 'success' });
      if (statut === 'Validé') ouvrirCommission({ ...dossier, statut });
    } catch {
      setToast({ message: 'Erreur lors de la mise à jour', type: 'error' });
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer ce dossier ?')) return;
    try {
      await axios.delete(`${API}/dossiers/${id}`);
      setDossiers(prev => prev.filter(d => d.id !== id));
      setToast({ message: 'Dossier supprimé', type: 'success' });
    } catch {
      setToast({ message: 'Erreur lors de la suppression', type: 'error' });
    }
  };

  const ouvrirEnvoiModal = (dossier) => {
    setEnvoiModal(dossier);
    setDestinataires([{ nom: '', email: '' }]);
    setMessage(
      `Bonjour,\n\nVeuillez trouver ci-joint le dossier de financement ${dossier.numero} concernant ${dossier.raison_sociale}.\n\nCordialement,\n${MARQUE.nom}`
    );
    setFichiers([]);
    setEnvoiResultat(null);
  };

  const envoyerDossier = async () => {
    const dests = destinataires.filter(d => d.email.trim());
    if (!dests.length) { setToast({ message: 'Ajoutez au moins un email destinataire', type: 'error' }); return; }
    setEnvoiLoading(true);
    try {
      const fd = new FormData();
      fd.append('destinataires', JSON.stringify(dests));
      fd.append('message', message);
      fichiers.forEach(f => fd.append('fichiers', f));
      const res = await axios.post(
        `${API}/dossiers/${envoiModal.id}/envoyer`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setEnvoiResultat(res.data);
      reloadEnvois();
    } catch (e) {
      setEnvoiResultat({ erreur: e.response?.data?.detail || 'Erreur lors de l\'envoi' });
    }
    setEnvoiLoading(false);
  };

  const inputStyle = champStyle;

  return (
    <div style={{ fontFamily: T.police }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={T.titre}>Dossiers de financement</h1>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>{dossiersFiltres.length} dossier(s)</p>
        </div>
      </div>

      {/* Filtres statut */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {STATUTS.map(s => {
          const active = filtreStatut === s;
          const sc = STATUT_COLORS[s];
          return (
            <button key={s} onClick={() => setFiltreStatut(s)} style={{
              padding: '7px 16px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: active ? '600' : '400',
              background: active && sc ? sc.bg : active ? '#1a2b4a' : 'white',
              color: active && sc ? sc.text : active ? 'white' : '#374151',
              border: active && sc ? `1px solid ${sc.border}` : active ? 'none' : '1px solid #e5e7eb',
            }}>
              {s}
              {s === ONGLET_REFUSES && refuses.length > 0 && ` (${refuses.length})`}
            </button>
          );
        })}
      </div>

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {['N°', 'Entreprise', 'SIRET', 'Montant HT', 'Commission', 'Type', 'Date', 'Statut', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          {chargement ? <SqueletteTableau lignes={5} colonnes={8} /> : (
          <tbody>
            {dossiersFiltres.length === 0 ? (
              <tr key="empty">
                <td colSpan={9} style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📁</div>
                  <p style={{ color: '#9ca3af', fontSize: '15px', margin: 0 }}>Aucun dossier{filtreStatut !== 'Tous' ? ` "${filtreStatut}"` : ''} pour l'instant.</p>
                  <p style={{ color: '#9ca3af', fontSize: '13px', margin: '6px 0 0' }}>Les dossiers sont créés automatiquement lors de la génération d'un PDF.</p>
                </td>
              </tr>
            ) : dossiersFiltres.map(d => {
              const sc = STATUT_COLORS[d.statut] || STATUT_COLORS['En cours'];
              const montant = d.montant_total ? Number(d.montant_total).toLocaleString('fr-FR') + ' €' : '—';
              const envois = envoisMap[d.id] || [];
              return (
                <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onClick={() => navigate(`/dossier?dossier_id=${d.id}`)}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontWeight: '700', fontSize: '13px', color: '#1a2b4a', fontFamily: 'monospace' }}>{d.numero}</span>
                      {envois.length > 0 && (
                        <span title={`Envoyé à ${envois.length} banque${envois.length > 1 ? 's' : ''} — dernier : ${new Date(envois[0].date_envoi).toLocaleDateString('fr-FR')}`}
                          style={{ fontSize: '13px', cursor: 'default' }}>📧</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '500', color: '#1a2b4a' }}>
                    {d.raison_sociale || '—'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280' }}>
                    {d.siret || '—'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                    {montant}
                  </td>
                  {/* La commission ne se saisit que sur un dossier validé :
                      avant, le montant n'est pas connu ; après un refus, il
                      n'a plus lieu d'être. */}
                  <td style={{ padding: '14px 16px', fontSize: '14px' }}
                      onClick={e => e.stopPropagation()}>
                    {d.statut !== 'Validé' ? (
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                    ) : (
                      <button onClick={() => ouvrirCommission(d)}
                        style={{ background: d.commission != null ? '#eff6ff' : '#fff7ed',
                                 border: `1px solid ${d.commission != null ? '#bfdbfe' : '#fed7aa'}`,
                                 color: d.commission != null ? '#1d4ed8' : '#9a3412',
                                 borderRadius: '6px', padding: '4px 10px', fontSize: '13px',
                                 fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {d.commission != null
                          ? Number(d.commission).toLocaleString('fr-FR') + ' €'
                          : 'À renseigner'}
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>
                    {TYPE_LABELS[d.type_financement] || d.type_financement || '—'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {new Date(d.date_creation).toLocaleDateString('fr-FR')}
                  </td>
                  <td style={{ padding: '14px 16px' }} onClick={e => e.stopPropagation()}>
                    <select
                      value={d.statut}
                      onChange={e => changerStatut(d, e.target.value)}
                      style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px', fontWeight: '600', background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, borderRadius: '99px', cursor: 'pointer' }}
                    >
                      {['En cours', 'Validé', 'Refusé'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '14px 16px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button
                        onClick={() => navigate(`/dossier?dossier_id=${d.id}`)}
                        style={{ background: 'white', color: '#1a2b4a', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>Modifier
                      </button>
                      <button
                        onClick={() => telechargerPDF(d)}
                        disabled={loadingId === d.id}
                        style={{ background: '#1a2b4a', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: '500', cursor: loadingId === d.id ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: loadingId === d.id ? 0.6 : 1 }}>
                        {loadingId === d.id ? '...' : '📥 PDF'}
                      </button>
                      <button
                        onClick={() => ouvrirEnvoiModal(d)}
                        title="Envoyer aux banques"
                        style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        📧
                      </button>
                      {user?.role === 'admin' && (
                        <button title="Supprimer"
                          onClick={() => supprimer(d.id)}
                          style={{ background: 'white', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}><IconeCorbeille taille={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          )}
        </table>
      </div>

      {/* Modal envoi */}
      {envoiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '560px', maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <h2 style={{ color: '#1a2b4a', fontSize: '17px', fontWeight: '600', margin: 0 }}>Envoyer aux banques</h2>
              <button onClick={() => setEnvoiModal(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 20px' }}>
              {envoiModal.numero} — {envoiModal.raison_sociale}
            </p>

            {envoiResultat ? (
              <div>
                {envoiResultat.erreur ? (
                  <div style={{ background: '#fff0f0', border: '1px solid #fecaca', borderRadius: '10px', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>❌</div>
                    <div style={{ color: '#991b1b', fontWeight: '600', fontSize: '14px' }}>{envoiResultat.erreur}</div>
                  </div>
                ) : (
                  <div style={{ background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ textAlign: 'center', marginBottom: envoiResultat.detail?.some(r => r.statut === 'error') ? '12px' : 0 }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                      <div style={{ color: '#065f46', fontWeight: '700', fontSize: '15px' }}>
                        Envoyé à {envoiResultat.envoyes} banque{envoiResultat.envoyes > 1 ? 's' : ''} sur {envoiResultat.total}
                      </div>
                    </div>
                    {envoiResultat.detail?.filter(r => r.statut === 'error').map((r, i) => (
                      <div key={i} style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', background: 'white', borderRadius: '6px', padding: '6px 10px' }}>{r.banque} ({r.email}) : {r.erreur}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setEnvoiResultat(null)}
                    style={{ background: 'white', color: '#374151', border: '1px solid #e5e7eb', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    Renvoyer
                  </button>
                  <button onClick={() => setEnvoiModal(null)}
                    style={{ background: '#1a2b4a', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Sélecteur rapide banques partenaires */}
                {banques.length > 0 && (
                  <div style={{ marginBottom: '16px', background: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Ajouter depuis les banques partenaires
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {[...banques].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map(b => {
                        const dejaPresent = destinataires.some(d => d.nom === b.nom || (b.email && d.email === b.email));
                        return (
                          <button key={b.id}
                            onClick={() => !dejaPresent && setDestinataires(p => {
                              const hasEmpty = p.some(d => !d.nom && !d.email);
                              if (hasEmpty) return p.map(d => (!d.nom && !d.email) ? { nom: b.nom, email: b.email || '' } : d);
                              return [...p, { nom: b.nom, email: b.email || '' }];
                            })}
                            disabled={dejaPresent}
                            style={{ background: dejaPresent ? '#f3f4f6' : 'white', color: dejaPresent ? '#9ca3af' : '#1a2b4a', border: `1px solid ${dejaPresent ? '#e5e7eb' : '#d1d5db'}`, borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: dejaPresent ? 'default' : 'pointer', fontWeight: '500', opacity: dejaPresent ? 0.6 : 1 }}>
                            {dejaPresent ? '✓ ' : '+ '}{b.nom}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Destinataires */}
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Destinataires</label>
                    <button onClick={() => setDestinataires(p => [...p, { nom: '', email: '' }])}
                      style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      + Ajouter
                    </button>
                  </div>
                  {destinataires.map((dest, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input
                        placeholder="Nom banque"
                        value={dest.nom}
                        onChange={e => setDestinataires(p => p.map((x, j) => j === i ? { ...x, nom: e.target.value } : x))}
                        style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }}
                      />
                      <input
                        placeholder="Email *"
                        type="email"
                        value={dest.email}
                        onChange={e => setDestinataires(p => p.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                        style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }}
                      />
                      {destinataires.length > 1 ? (
                        <button onClick={() => setDestinataires(p => p.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '4px', lineHeight: 1 }}>
                          ✕
                        </button>
                      ) : <span />}
                    </div>
                  ))}
                </div>

                {/* Message */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Message</label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={5}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Pièces jointes */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Pièces jointes</label>
                    <button onClick={() => fichierRef.current?.click()}
                      style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      + Ajouter
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 8px' }}>Le PDF du dossier est joint automatiquement. Ajoutez RIB, bilans, K-bis, etc. si besoin.</p>
                  <input
                    type="file" multiple ref={fichierRef} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
                    onChange={e => setFichiers(prev => [...prev, ...Array.from(e.target.files)])}
                    style={{ display: 'none' }}
                  />
                  {fichiers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {fichiers.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px' }}>
                          <span style={{ fontSize: '14px' }}>{f.name.toLowerCase().endsWith('.pdf') ? '📄' : f.name.match(/\.(doc|docx)$/i) ? '📝' : '📎'}</span>
                          <div style={{ flex: 1, fontSize: '12px', fontWeight: '500', color: '#1a2b4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                          <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} Ko</span>
                          <button onClick={() => setFichiers(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '2px', flexShrink: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {fichiers.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#d1d5db', fontStyle: 'italic' }}>Aucune pièce jointe ajoutée.</div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={envoyerDossier}
                    disabled={envoiLoading}
                    style={{ flex: 1, background: '#1a2b4a', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', cursor: envoiLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: envoiLoading ? 0.7 : 1 }}>
                    {envoiLoading
                      ? 'Envoi en cours...'
                      : `📧 Envoyer à ${destinataires.filter(d => d.email.trim()).length} banque${destinataires.filter(d => d.email.trim()).length > 1 ? 's' : ''}`}
                  </button>
                  <button
                    onClick={() => setEnvoiModal(null)}
                    style={{ padding: '12px 20px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Commission d'un dossier validé. S'ouvre seule à la validation, et se
          rouvre depuis la colonne « Commission » pour corriger un montant. */}
      {commissionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
             onClick={() => setCommissionModal(null)}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '420px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#1a2b4a', margin: '0 0 6px' }}>
              Commission du dossier
            </h2>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '18px' }}>
              {commissionModal.numero} — <strong style={{ color: '#1a2b4a' }}>{commissionModal.raison_sociale}</strong>
              {commissionModal.montant_total ? ` · ${Number(commissionModal.montant_total).toLocaleString('fr-FR')} € HT financés` : ''}
            </div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500',
                            color: '#6b7280', marginBottom: '4px' }}>
              Montant de la commission (€)
            </label>
            <input type="number" min="0" step="0.01" autoFocus
              value={commissionSaisie}
              onChange={e => setCommissionSaisie(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') enregistrerCommission(); }}
              placeholder="Ex : 2500"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px',
                       border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none',
                       boxSizing: 'border-box' }} />
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0' }}>
              Ce montant alimente le CA prévisionnel du tableau de bord, et le montant
              HT financé le volume financé. Laisser vide efface la commission.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={enregistrerCommission} disabled={commissionEnvoi}
                style={{ flex: 1, background: '#1a2b4a', color: 'white', padding: '11px',
                         border: 'none', borderRadius: '8px', cursor: 'pointer',
                         fontSize: '14px', fontWeight: '500' }}>
                {commissionEnvoi ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button onClick={() => setCommissionModal(null)}
                style={{ padding: '11px 20px', background: 'white', color: '#6b7280',
                         border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                         fontSize: '14px' }}>
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default Dossiers;
